/**
 * `/d/:department/r/:slug` — the generic report renderer (SPEC §9.1, §9.4).
 *
 * **The parameters live in the URL and nowhere else.** This component reads
 * them from the query string, hands them to the ParamBar, and writes every
 * edit straight back. There is no second copy in component state that could
 * disagree, which is exactly what makes the acceptance test hold: *a filtered
 * report URL sent to someone else reproduces the same view.*
 *
 * On first load it resolves every parameter — from the URL, then the manifest
 * default, then a usable stand-in — and rewrites the address bar once with the
 * full set, using `replace` so the bare link does not become a back-button
 * step. A link therefore pins what its sender was looking at even if the
 * manifest default changes later.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { ApiError, api } from '../lib/api'
import { departmentAccent } from '../lib/charts'
import {
  fromSearchParams,
  isRunnable,
  toRequestParams,
  toSearchParams,
} from '../lib/params'
import { formatNumber } from '../lib/format'
import { ME_STALE_MS } from '../lib/session'
import { CUSTOM_REPORTS, CUSTOM_REPORTS_PLACING_CAVEATS } from '../custom-reports'
import { CaveatList } from '../components/CaveatList'
import { EmptyState, ErrorState, Skeleton } from '../components/States'
import { FreshnessBadge } from '../components/FreshnessBadge'
import { ParamBar } from '../components/ParamBar'
import { VisualHost } from '../components/VisualHost'

export function Report() {
  const { department = '', slug = '' } = useParams()
  const [search, setSearch] = useSearchParams()

  const queryClient = useQueryClient()

  // A reader-triggered snapshot reload (§9.4). On success every cached answer
  // for this report is invalidated, because the frame they were derived from is
  // gone — the server has already keyed them out, and this makes the page catch
  // up without a reload.
  const refresh = useMutation({
    mutationFn: () => api.refreshReport(slug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['run', slug] }),
  })

  const definitionQuery = useQuery({
    queryKey: ['report', slug],
    queryFn: () => api.report(slug),
    retry: false,
  })
  const definition = definitionQuery.data

  // The chart primary series is the department's accent (§9.2). Read the served
  // value from the cached `/api/me` — the same query the shell loaded — so an
  // admin recolour (which invalidates `['me']`) reaches the generic charts. The
  // seed map is only a fallback until that data is present. The bespoke reports
  // below take the department *key* instead; they colour through CSS variables.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    staleTime: ME_STALE_MS,
    retry: false,
  })
  const accent = useMemo(
    () =>
      me?.departments.find((entry) => entry.key === department)?.accent ??
      departmentAccent(department),
    [me, department],
  )

  // Values are derived from the URL every render — the URL is the state.
  const values = useMemo(
    () =>
      definition === undefined ? {} : fromSearchParams(definition.parameters, search),
    [definition, search],
  )

  // Materialise the resolved set into the address bar, once, on load.
  useEffect(() => {
    if (definition === undefined) return
    const canonical = toSearchParams(definition.parameters, values).toString()
    if (canonical !== search.toString()) {
      setSearch(new URLSearchParams(canonical), { replace: true })
    }
  }, [definition, values, search, setSearch])

  const runnable = definition !== undefined && isRunnable(definition.parameters, values)
  const requestParams = useMemo(
    () => (definition === undefined ? {} : toRequestParams(definition.parameters, values)),
    [definition, values],
  )

  // An uncached report waits to be asked (§9.4); everything else runs as soon
  // as its parameters are valid.
  const manualRun = definition?.cache_ttl_seconds === 0
  const [runToken, setRunToken] = useState(0)
  const runNow = useCallback(() => {
    setRunToken((token) => token + 1)
  }, [])

  const resultQuery = useQuery({
    // `runToken` is in the key so an explicit Run refetches even when nothing
    // else changed — "run it again" is a real request on a live report.
    queryKey: ['run', slug, requestParams, runToken],
    queryFn: () => api.runReport(slug, requestParams),
    enabled: runnable && (!manualRun || runToken > 0),
    retry: false,
    placeholderData: (previous) => previous,
  })

  if (definitionQuery.isPending) return <Skeleton rows={4} />
  if (definitionQuery.error !== null) return <ErrorState error={definitionQuery.error} />
  if (definition === undefined) return null

  // Only a payload for *this* report may render. `placeholderData` deliberately
  // keeps the previous query's result across a key change so month/filter edits
  // within a report don't flash a skeleton — but across *different* reports that
  // held-over data has a different column set, and a custom report reading it by
  // column key would render a table of blank rows until the real run resolves.
  // The §8.6 response carries its own slug; trust it, and treat a mismatch as
  // "still loading" so the skeleton shows instead.
  const result = resultQuery.data?.slug === slug ? resultQuery.data : undefined
  const runError = resultQuery.error
  const fieldErrors = runError instanceof ApiError ? runError.fields : {}
  const Custom = CUSTOM_REPORTS[slug]

  // §9.3: the freshness badge (with the refresh control on a snapshot report)
  // now lives in the filter bar, right-aligned — the reference's one-bar
  // layout. It still shows before the first run, when it says so plainly.
  const freshness = (
    <FreshnessBadge
      dataAsOf={result?.data_as_of ?? null}
      error={result?.data_as_of_error ?? null}
      warnAfterSeconds={definition.freshness_warn_after_seconds}
      kind={result?.data_as_of_kind ?? null}
      snapshotTtlSeconds={definition.snapshot_ttl_seconds}
      // Only a snapshot report with `reader_refresh` gets the control.
      onRefresh={definition.reader_refresh ? refresh.mutate : undefined}
      refreshing={refresh.isPending}
    />
  )

  return (
    <div className="space-y-4">
      <nav className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
        <Link to="/" className="hover:text-[var(--color-ink)]">
          Departments
        </Link>
        <span aria-hidden> / </span>
        <Link to={`/d/${department}`} className="hover:text-[var(--color-ink)]">
          {department}
        </Link>
      </nav>

      <header className="min-w-0">
        <h1 className="flex items-center gap-2 text-[length:var(--text-lg)] font-semibold tracking-tight">
          <span aria-hidden className="h-5 w-1 rounded-full bg-[var(--accent)]" />
          {definition.title}
        </h1>
        <p className="mt-1 text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
          {definition.summary}
        </p>
      </header>

      <ParamBar
        slug={slug}
        specs={definition.parameters}
        values={values}
        fieldErrors={fieldErrors}
        cacheTtlSeconds={definition.cache_ttl_seconds}
        // §8.9: a snapshot report's pickers are filled from the run payload,
        // which is why there is no second request to fill them.
        datasets={result?.datasets}
        onChange={(next) => {
          setSearch(toSearchParams(definition.parameters, next))
        }}
        onRun={runNow}
        isRunning={resultQuery.isFetching}
        freshness={freshness}
      />

      {!runnable && (
        <EmptyState cause="Set the required parameters above to run this report." />
      )}

      {runError !== null && (
        <ErrorState
          error={runError}
          onRetry={() => {
            void resultQuery.refetch()
          }}
        />
      )}

      {runnable && result === undefined && runError === null && <Skeleton rows={3} />}

      {result !== undefined && runError === null && (
        <>
          {/* §9.6: data-quality figures are stated under the title, not
              buried. The sentence is rendered server-side so the wording and
              the figure cannot drift apart. */}
          {/* A bespoke report may opt to place them itself, at its foot. */}
          {!CUSTOM_REPORTS_PLACING_CAVEATS.has(slug) && <CaveatList caveats={result.caveats} />}

          {/* §8.7: the bespoke path gets the same payload, not its own
              endpoint — and it is checked *before* the empty-primary case. A
              custom report owns several datasets, so an empty `primary` says
              nothing about whether it has something to render; suppressing the
              component there would blank a page whose chart and pickers are
              fine, and leave it unable to say which filter emptied it. */}
          {Custom !== undefined ? (
            <Custom
              result={result}
              department={department}
              values={values}
              setParam={(name, value) => {
                setSearch(toSearchParams(definition.parameters, { ...values, [name]: value }))
              }}
            />
          ) : result.rows.length === 0 ? (
            <EmptyState
              cause={
                definition.parameters.length === 0
                  ? 'This report returned no rows.'
                  : 'No rows for these parameters — try widening the date range or clearing a filter.'
              }
            />
          ) : (
            <VisualHost
              visuals={definition.visuals}
              columns={result.columns}
              rows={result.rows}
              accent={accent}
              reportTitle={definition.title}
              datasets={result.datasets}
              // §9.5: a click writes into a real parameter, which lives in the
              // URL and nowhere else — so the drilled-down view is a link, and
              // there is no second copy of report state to drift.
              onSelect={(param, value) => {
                setSearch(
                  toSearchParams(definition.parameters, { ...values, [param]: String(value) }),
                )
              }}
            />
          )}

          <footer className="numeric flex flex-wrap gap-x-4 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
            <span>{formatNumber(result.row_count)} rows</span>
            <span>{formatNumber(result.duration_ms)} ms</span>
            <span>{result.cache_hit ? 'from cache' : 'freshly queried'}</span>
            <span>owner: {definition.owner}</span>
          </footer>
        </>
      )}
    </div>
  )
}
